
import { useAuth } from "@/hooks/useAuth";
import { useNonLoggedToolUsage, IncrementUsageResult } from "@/hooks/useNonLoggedToolUsage";
import { toast } from "sonner";

/**
 * Hook to track tool actions for non-logged users
 * Returns a function that can be called to track an action
 */
export function useToolActionTracker(toolSlug: string) {
  const { user } = useAuth();
  const nonLoggedUsage = useNonLoggedToolUsage(toolSlug);

  return (actionName: string = "click"): IncrementUsageResult => {
    // If user is logged in, don't track
    if (user) return { success: true, limitReached: false, reason: null };
    
    try {
      // Increment usage with the new return type
      const result = nonLoggedUsage.incrementUsage();
      
      // Handle limit reached
      if (result.limitReached) {
        toast.error("Limite d'utilisation gratuite atteinte. Créez un compte pour continuer.");
      }
      
      if (result.success) {
        console.log(`[TRACK] ${toolSlug} / ${actionName} +1`);
      }
      
      return result;
    } catch (error) {
      console.error(`Erreur lors du tracking de l'action ${actionName} pour ${toolSlug}:`, error);
      // Retourner un résultat par défaut en cas d'erreur
      return { success: false, limitReached: false, reason: null };
    }
  };
}

/**
 * Standalone function to track tool actions
 * Can be used outside of React components
 */
export function trackToolAction(toolSlug: string, actionName: string = "click"): IncrementUsageResult {
  try {
    // Get usage data from localStorage
    const storageKey = 'aluna-nonlogged-tool-usages';
    const stored = localStorage.getItem(storageKey);
    const usages = stored ? JSON.parse(stored) : {};
    const currentUsage = usages[toolSlug] || 0;
    
    // Check if localStorage has auth data to determine if user is logged in
    const hasAuthSession = !!localStorage.getItem('sb-supabase-auth-token');
    if (hasAuthSession) return { success: true, limitReached: false, reason: null };
    
    // Anti-spam check
    const now = Date.now();
    const lastAction = parseInt(localStorage.getItem('aluna-last-action-timestamp') || '0', 10);
    if (now - lastAction < 2000) {
      return { success: false, limitReached: false, reason: "anti-spam" };
    }
    
    // Check if limit reached
    const MAX_USES = 5;
    if (currentUsage >= MAX_USES) {
      return { success: false, limitReached: true, reason: "quota" };
    }
    
    // Update usage count
    const newCount = currentUsage + 1;
    usages[toolSlug] = newCount;
    localStorage.setItem(storageKey, JSON.stringify(usages));
    localStorage.setItem('aluna-last-action-timestamp', now.toString());
    
    console.log(`[TRACK] ${toolSlug} / ${actionName} +1 (direct)`);
    
    return { 
      success: true, 
      limitReached: newCount >= MAX_USES,
      reason: null
    };
  } catch (error) {
    console.error(`Erreur lors du tracking de l'action ${actionName} pour ${toolSlug}:`, error);
    // Retourner un résultat par défaut en cas d'erreur
    return { success: false, limitReached: false, reason: null };
  }
}

/**
 * Function to reset all usage counts (for staging/testing)
 * This can be called from the console with: resetAllUsages()
 */
export function resetAllUsages() {
  // Reset tool-specific usages
  localStorage.removeItem('aluna-nonlogged-tool-usages');
  
  // Reset global free usages
  localStorage.removeItem('aluna-free-tool-usages');
  
  // Reset tool-specific limits
  localStorage.removeItem('aluna-tool-specific-usages');
  
  // Reset visited tools
  localStorage.removeItem('aluna-visited-tools');
  
  // Reset action timestamp
  localStorage.removeItem('aluna-last-action-timestamp');
  
  console.log('🧹 All usage counts have been reset!');
  
  // Return true to confirm reset was done
  return true;
}

// Exposer resetAllUsages uniquement en dev/staging
if (typeof window !== 'undefined') {
  const isDevOrStaging =
    import.meta.env.DEV ||
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('staging');

  if (isDevOrStaging) {
    (window as Window & { resetAllUsages?: typeof resetAllUsages }).resetAllUsages = resetAllUsages;

    if (window.location.hostname.includes('staging') || window.location.hostname.includes('localhost')) {
      window.addEventListener('load', () => {
        resetAllUsages();
        console.log('🧪 Staging environment detected - usage counts automatically reset');
      });
    }
  }
}
